/**
 * The part's script, edited together (PLAN §6.2 "Two people edit the same
 * script": Yjs merges character by character). Every input emits the text
 * the user typed into and the text it became (`onChange(prev, next)`); the
 * view replays that one edit onto the newest text (`rebaseEdit` — others'
 * typing may have landed in between) and turns it into a Yjs update of just
 * the changed span (`lib/editor/patches.ts`'s `textPatch`). The merged text
 * comes back as `text`.
 *
 * The textarea is deliberately **uncontrolled**: when `text` changes because
 * of someone else's typing, the new text is written into it directly and the
 * caret is moved by `transformSelection`, so it stays where the user was
 * typing instead of jumping to the end. Ctrl/⌘+Z are the editor's undo, not
 * the browser's (whose history the remote writes would corrupt).
 */
import { type KeyboardEvent, useLayoutEffect, useRef, useState } from 'react';
import { transformSelection } from '../../lib/editor/caret.js';
import type { RowPresence } from '../../lib/editor/presence.js';
import { cn } from '../../lib/utils.js';
import { Label } from '../ui/label.js';
import { presenceClass } from './PresenceAvatars.js';

export type ScriptPanelProps = {
  readonly text: string;
  /** Others editing this script right now. */
  readonly editors: readonly RowPresence[];
  /** `prev`: the text as the textarea showed it before this input; `next`: after. */
  readonly onChange: (prev: string, next: string) => void;
  readonly onFocusChange: (focused: boolean) => void;
  readonly onUndo: () => void;
  readonly onRedo: () => void;
};

export function ScriptPanel({ text, editors, onChange, onFocusChange, onUndo, onRedo }: ScriptPanelProps) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  /** What the textarea showed after the last input or sync: the base of the next input. */
  const shown = useRef(text);
  // The mount-time text only: a CHANGING `defaultValue` makes React rewrite an
  // untouched textarea's value itself, behind `shown`'s back (the two-user e2e
  // test caught the duplicated text that caused).
  const [initial] = useState(text);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null) return;
    shown.current = text;
    if (el.value === text) return;
    const focused = el.ownerDocument.activeElement === el;
    const selection = transformSelection(el.value, text, { start: el.selectionStart, end: el.selectionEnd });
    el.value = text;
    if (focused) el.setSelectionRange(selection.start, selection.end);
  }, [text]);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
    const key = e.key.toLowerCase();
    if (key === 'z') {
      e.preventDefault();
      if (e.shiftKey) onRedo();
      else onUndo();
    } else if (key === 'y') {
      e.preventDefault();
      onRedo();
    }
  };

  return (
    <section className="grid gap-1 border-t pt-3" data-testid="script-panel" aria-label="Script">
      <div className="flex items-center gap-2">
        <Label htmlFor="part-script" className="text-xs text-muted-foreground">
          Script
        </Label>
        {editors.length === 0 ? null : (
          <span className="ml-auto flex items-center gap-1 text-xs text-muted-foreground" data-testid="script-editors">
            {editors.map((p) => (
              <span key={p.userId} className={cn('rounded-sm px-1', presenceClass(p.colour))}>
                {p.initials}
              </span>
            ))}
            typing
          </span>
        )}
      </div>
      <textarea
        ref={ref}
        id="part-script"
        data-testid="script-text"
        defaultValue={initial}
        rows={10}
        spellCheck
        placeholder="What the presenter reads…"
        className="min-h-40 w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 font-mono text-sm leading-relaxed shadow-xs outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
        onInput={(e) => {
          const prev = shown.current;
          shown.current = e.currentTarget.value;
          onChange(prev, e.currentTarget.value);
        }}
        onFocus={() => onFocusChange(true)}
        onBlur={() => onFocusChange(false)}
        onKeyDown={onKeyDown}
      />
    </section>
  );
}
